import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart3, ArrowUpDown, Download, Search, Rocket, Receipt } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import {
  Button,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import {
  DataTable,
  EmptyState,
  Pagination,
  type ColumnDef,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { WaapEmptyCard } from './WaapEmptyCard';
import BusinessUsageShell from '@/pages/services/usage/BusinessUsageShell';
import {
  useWaapBusinessUsage,
  useWaapBillingUsage,
  type WaapBusinessUsageRow,
} from '@/api/waap/usage';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import styles from './BusinessUsageTab.module.css';

const PAGE_SIZE = 25;

// ---- Formatters ----------------------------------------------------------
// Vendor units: volume = bytes, bandwidth = bits/sec, rps = requests/sec.
// Scaled with decimal (SI) factors of 1000, matching the portal display.
function fmtVolume(bytes?: number): string {
  if (bytes == null || !isFinite(bytes)) return '—';
  const u = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  let v = bytes;
  let i = 0;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

function fmtRate(bps?: number): string {
  if (bps == null || !isFinite(bps)) return '—';
  const u = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps'];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < u.length - 1) {
    v /= 1000;
    i++;
  }
  return `${v.toFixed(2)} ${u[i]}`;
}

const fmtQps = (rps?: number): string =>
  rps == null || !isFinite(rps) ? '—' : `${rps.toFixed(2)} qps`;

/** Two-label public suffixes we serve domains under (fallback root-domain
 *  derivation only — good enough for the summary card, not a full PSL). */
const TWO_LABEL_TLDS = new Set([
  'com.vn', 'net.vn', 'org.vn', 'edu.vn', 'gov.vn', 'ac.vn', 'biz.vn', 'info.vn',
  'co.uk', 'com.au', 'co.jp', 'com.sg', 'com.my', 'co.th', 'co.id', 'com.kh',
]);

/** Registrable root of a hostname: last 2 labels, or 3 when the suffix is a
 *  two-label TLD (vmobi-api.acbs.com.vn → acbs.com.vn). */
function rootDomainOf(host: string): string {
  const parts = host.split('.').filter(Boolean);
  if (parts.length <= 2) return host;
  const lastTwo = parts.slice(-2).join('.');
  const take = TWO_LABEL_TLDS.has(lastTwo) ? 3 : 2;
  return parts.slice(-take).join('.');
}

type SortKey = 'domain' | 'volume' | 'bandwidth' | 'rps';

/** Last 12 months as { value: "YYYY-MM", label: "June 2026" }, newest first. */
function recentMonths(): { value: string; label: string }[] {
  const now = new Date();
  const out: { value: string; label: string }[] = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
}

/**
 * WAAP Business Usage — account-level monthly usage metering. Two private reads
 * (anchored on any one website; the data is account-wide):
 *   - billing-usage  → the 5 summary cards (root domains / domains / total
 *                      volume / peak bandwidth / peak QPS)
 *   - business-usage → the per-domain table (volume / peak bandwidth / QPS)
 */
export function BusinessUsageTab() {
  const navigate = useNavigate();
  const { serviceId } = useParams<{ serviceId: string }>();
  const { websites, isLoading: wsLoading } = useWaapWebsite();
  const anchor = websites[0]?.domainId;

  const months = useMemo(recentMonths, []);
  const [month, setMonth] = useState(() => months[0].value);

  const business = useWaapBusinessUsage(anchor, month);
  const billing = useWaapBillingUsage(anchor, month);

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('volume');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows = business.data ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? rows.filter((r) => (r.domain ?? '').toLowerCase().includes(q)) : rows;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...base].sort((a, b) => {
      if (sortKey === 'domain') return (a.domain ?? '').localeCompare(b.domain ?? '') * dir;
      return (((a[sortKey] as number) ?? 0) - ((b[sortKey] as number) ?? 0)) * dir;
    });
  }, [rows, query, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (k: SortKey) => {
    setPage(1);
    if (k === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(k);
      setSortDir(k === 'domain' ? 'asc' : 'desc');
    }
  };

  const sortHeader = (label: string, k: SortKey) => (
    <button type="button" onClick={() => toggleSort(k)} className={styles.sortButton}>
      {label}
      <ArrowUpDown size={12} style={{ opacity: sortKey === k ? 1 : 0.4 }} />
    </button>
  );

  const columns = useMemo<ColumnDef<WaapBusinessUsageRow>[]>(
    () => [
      {
        accessorKey: 'domain',
        header: () => sortHeader('Domain Name', 'domain'),
        cell: ({ row }) => <span className={styles.name}>{row.original.domain}</span>,
      },
      {
        accessorKey: 'volume',
        header: () => sortHeader('Volume', 'volume'),
        cell: ({ row }) => fmtVolume(row.original.volume),
      },
      {
        accessorKey: 'bandwidth',
        header: () => sortHeader('Peak Bandwidth', 'bandwidth'),
        cell: ({ row }) => fmtRate(row.original.bandwidth),
      },
      {
        accessorKey: 'rps',
        header: () => sortHeader('Peak Query per Second (QPS)', 'rps'),
        cell: ({ row }) => fmtQps(row.original.rps),
      },
    ],
    // header closures capture sort state; rebuild when it changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sortKey, sortDir],
  );

  const exportCsv = () => {
    const header = ['Domain Name', 'Volume (bytes)', 'Peak Bandwidth (bps)', 'Peak QPS'];
    const lines = filtered.map((r) =>
      [r.domain ?? '', r.volume ?? '', r.bandwidth ?? '', r.rps ?? ''].join(','),
    );
    const csv = [header.join(','), ...lines].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `business-usage-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Billing summary with per-field fallback: some months the vendor's
  // billing-usage read comes back empty while business-usage still has
  // per-domain rows (e.g. a closed prior month) — derive the cards from the
  // table then instead of showing em dashes.
  const summary = useMemo(() => {
    const s = billing.data;
    if (!rows.length) return s;
    const domains = new Set<string>();
    const roots = new Set<string>();
    let volume = 0;
    let bandwidth = 0;
    let rps = 0;
    for (const r of rows) {
      const d = (r.domain ?? '').trim().toLowerCase();
      if (d) {
        domains.add(d);
        roots.add(rootDomainOf(d));
      }
      volume += r.volume ?? 0;
      bandwidth = Math.max(bandwidth, r.bandwidth ?? 0);
      rps = Math.max(rps, r.rps ?? 0);
    }
    return {
      root_domains: s?.root_domains ?? roots.size,
      domains: s?.domains ?? domains.size,
      volume: s?.volume ?? volume,
      bandwidth: s?.bandwidth ?? bandwidth,
      rps: s?.rps ?? rps,
    };
  }, [billing.data, rows]);
  const loading = wsLoading || business.isLoading || billing.isLoading;
  const isError = business.isError || billing.isError;

  // Headline KPI strip — same StatGroup basis as before, via the shared KpiTiles
  // so every service's Business Usage renders its summary identically.
  const kpis = [
    { label: 'Root Domains', value: summary?.root_domains ?? '—' },
    { label: 'Domains', value: summary?.domains ?? '—' },
    { label: 'Total Volume', value: fmtVolume(summary?.volume) },
    { label: 'Peak Bandwidth', value: fmtRate(summary?.bandwidth) },
    { label: 'Peak QPS', value: fmtQps(summary?.rps) },
  ];

  return !wsLoading && !anchor ? (
    <Container size="xl">
      <PageHeader
        icon={<Receipt size={22} />}
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Business Usage' }]} />}
        title="Business Usage"
        description="Account-wide monthly volume, peak bandwidth and peak QPS per domain."
      />
      <WaapEmptyCard
        icon={<BarChart3 size={20} />}
        title="No websites yet"
        description="Onboard a website to start accelerating and protecting it with WAAP."
        actions={
          <Button
            startIcon={<Rocket size={16} />}
            onClick={() => navigate(`/services/waap/${serviceId}/onboarding`)}
          >
            Onboarding Domain Now
          </Button>
        }
      />
    </Container>
  ) : (
    <BusinessUsageShell
      icon={<Receipt size={22} />}
      breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Business Usage' }]} />}
      description="Account-wide monthly volume, peak bandwidth and peak QPS per domain."
      basis="Account-wide monthly metering aggregated across every protected website — total volume, peak bandwidth and peak QPS, reconciled per calendar month."
      extraAlert={
        isError ? (
          <Alert tone="warning" title="Usage data unavailable">
            Couldn't load business usage for this month. Please try again shortly.
          </Alert>
        ) : undefined
      }
      period={
        <Select
          value={month}
          onValueChange={(v) => {
            setMonth(v);
            setPage(1);
          }}
        >
          <SelectTrigger size="md" style={{ width: 170, maxWidth: '100%' }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {months.map((m) => (
              <SelectItem key={m.value} value={m.value}>
                {m.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
      actions={
        <Button
          variant="secondary"
          size="md"
          startIcon={<Download size={14} />}
          onClick={exportCsv}
          disabled={!filtered.length}
        >
          Export CSV
        </Button>
      }
      kpis={kpis}
      kpisLoading={loading}
      periodLabel={months.find((m) => m.value === month)?.label ?? month}
      search={
        <Input
          inputSize="md"
          placeholder="Filter by domain…"
          startSlot={<Search />}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          style={{ width: 260, maxWidth: '100%' }}
        />
      }
    >
      {loading ? (
        <Skeleton className={styles.skeletonBlock} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={paged}
            emptyState={
              <EmptyState
                size="sm"
                icon={<BarChart3 size={20} />}
                title="No usage"
                description="No usage records for the selected month."
              />
            }
          />
          {filtered.length > 0 && (
            <div className={styles.pagination}>
              <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </BusinessUsageShell>
  );
}
